/**
 * Point-in-polygon algorithm using ray casting
 * Determines if a point is inside a polygon
 * @param point - [longitude, latitude]
 * @param polygon - Array of [longitude, latitude] coordinates
 * @returns true if point is inside polygon
 */
export function isPointInPolygon(point: [number, number], polygon: number[][]): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Parse coordinate string from KML format and extract coordinate pairs
 * @param coordString - Coordinate string from KML (e.g., "6.123,46.456,0.0 6.789,46.012,0.0")
 * @returns Array of [longitude, latitude] pairs
 */
export function parseKMLCoordinates(coordString: string): number[][] {
  return coordString.trim().split(' ')
    .map(coord => {
      const parts = coord.split(',');
      return [parseFloat(parts[0]), parseFloat(parts[1])];
    })
    .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
}

/**
 * Extract all boundary segments from KML content
 * @param kmlContent - Raw KML file content
 * @returns Array of coordinate segments that form the boundary
 */
export function extractBoundarySegments(kmlContent: string): number[][][] {
  const segments: number[][][] = [];
  
  // Match all <coordinates>...</coordinates> blocks
  const coordinateRegex = /<coordinates>(.*?)<\/coordinates>/g;
  let match;
  
  while ((match = coordinateRegex.exec(kmlContent)) !== null) {
    const coordString = match[1];
    const coordinates = parseKMLCoordinates(coordString);
    if (coordinates.length > 0) {
      segments.push(coordinates);
    }
  }
  
  return segments;
}

/**
 * Connect boundary segments to form closed polygons
 * @param segments - Array of coordinate segments
 * @returns Array of closed polygons
 */
export function connectBoundarySegments(segments: number[][][]): number[][][] {
  if (segments.length === 0) return [];
  
  const polygons: number[][][] = [];
  const usedSegments = new Set<number>();
  
  for (let i = 0; i < segments.length; i++) {
    if (usedSegments.has(i)) continue;
    
    const polygon: number[][] = [...segments[i]];
    usedSegments.add(i);
    
    // Try to connect segments to form a closed polygon
    let foundConnection = true;
    while (foundConnection) {
      foundConnection = false;
      const lastPoint = polygon[polygon.length - 1];
      
      // Find a segment that starts where this one ends
      for (let j = 0; j < segments.length; j++) {
        if (usedSegments.has(j)) continue;
        
        const segment = segments[j];
        const segmentStart = segment[0];
        const segmentEnd = segment[segment.length - 1];
        
        // Check if this segment connects to our polygon
        const tolerance = 0.000001; // Small tolerance for floating point comparison
        
        if (Math.abs(lastPoint[0] - segmentStart[0]) < tolerance && 
            Math.abs(lastPoint[1] - segmentStart[1]) < tolerance) {
          // Connect forward
          polygon.push(...segment.slice(1));
          usedSegments.add(j);
          foundConnection = true;
          break;
        } else if (Math.abs(lastPoint[0] - segmentEnd[0]) < tolerance && 
                   Math.abs(lastPoint[1] - segmentEnd[1]) < tolerance) {
          // Connect backward (reverse the segment)
          polygon.push(...segment.slice(0, -1).reverse());
          usedSegments.add(j);
          foundConnection = true;
          break;
        }
      }
    }
    
    polygons.push(polygon);
  }
  
  return polygons;
}

/**
 * Check if coordinates are within Geneva canton using the official KML boundary data
 * @param longitude - Longitude coordinate
 * @param latitude - Latitude coordinate
 * @returns Promise that resolves to true if coordinates are within Geneva canton
 */
export async function isWithinGenevaCanton(longitude: number, latitude: number): Promise<boolean> {
  try {
    // Fetch the KML file from the public directory
    const response = await fetch('/CAD_LIMITE_CANTON.kml');
    if (!response.ok) {
      console.warn('Could not load Geneva canton boundary KML file');
      return false;
    }
    
    const kmlContent = await response.text();
    
    // Extract boundary segments from KML
    const segments = extractBoundarySegments(kmlContent);
    
    // Connect segments to form polygons
    const polygons = connectBoundarySegments(segments);
    
    // Check if the point is inside any of the polygons
    const point: [number, number] = [longitude, latitude];
    
    for (const polygon of polygons) {
      if (polygon.length >= 3 && isPointInPolygon(point, polygon)) {
        return true;
      }
    }
    
    return false;
    
  } catch (error) {
    console.error('Error checking Geneva canton boundary:', error);
    // Fallback: return false if we can't check
    return false;
  }
}

// Helper functions for testing in browser console
export const GenevaTestUtils = {
  // Simple test method for any coordinates
  testPoint: async (longitude: number, latitude: number) => {
    console.log(`Testing coordinates: [${longitude}, ${latitude}]`);
    try {
      const result = await isWithinGenevaCanton(longitude, latitude);
      console.log(`Result: ${result ? '✅ INSIDE Geneva canton' : '❌ OUTSIDE Geneva canton'}`);
      return result;
    } catch (error) {
      console.error('Error testing point:', error);
      return false;
    }
  },

  // Quick test function for simple polygon
  testSimplePolygon: () => {
    const simpleSquare = [
      [6.0, 46.0],
      [6.5, 46.0], 
      [6.5, 46.5],
      [6.0, 46.5],
      [6.0, 46.0] // Close the polygon
    ];
    
    console.log('Testing simple square polygon...');
    console.log('Point [6.25, 46.25] (should be inside):', isPointInPolygon([6.25, 46.25], simpleSquare));
    console.log('Point [5.5, 45.5] (should be outside):', isPointInPolygon([5.5, 45.5], simpleSquare));
    console.log('Point [6.0, 46.0] (on boundary):', isPointInPolygon([6.0, 46.0], simpleSquare));
  },
  
  // Test with Geneva center coordinates
  testGenevaCoordinates: async () => {
    const genevaCenter = [6.1432, 46.2044]; // Geneva city center
    const vevey = [6.8447, 46.4607]; // Vevey (outside Geneva)
    const lausanne = [6.6323, 46.5197]; // Lausanne (outside Geneva)
    
    console.log('Testing Geneva coordinates...');
    console.log('Geneva center:', await isWithinGenevaCanton(genevaCenter[0], genevaCenter[1]));
    console.log('Vevey (should be false):', await isWithinGenevaCanton(vevey[0], vevey[1]));
    console.log('Lausanne (should be false):', await isWithinGenevaCanton(lausanne[0], lausanne[1]));
  },
  
  // Load and inspect KML data
  inspectKMLData: async () => {
    try {
      const response = await fetch('/CAD_LIMITE_CANTON.kml');
      const kmlContent = await response.text();
      const segments = extractBoundarySegments(kmlContent);
      const polygons = connectBoundarySegments(segments);
      
      console.log('KML Data Analysis:');
      console.log('Number of coordinate segments:', segments.length);
      console.log('Number of connected polygons:', polygons.length);
      console.log('Largest polygon points:', Math.max(...polygons.map(p => p.length)));
      console.log('Sample segments (first 3):', segments.slice(0, 3));
      
      return { segments, polygons, kmlContent };
    } catch (error) {
      console.error('Error loading KML:', error);
    }
  }
}; 